import type { BankOfChangesCopy } from './bankOfChangesCopy';
import type {
  BankActivity,
  BankActivityCluster,
  BankChatTurn,
  BankConversationInfo,
  BankGeneratedFile,
  BankOutputFile,
  BankServerRun,
  BankSessionState,
  BankStoredFile,
  BankUpload,
  BankVersionFiles,
} from './types';

export type { BankOutputFile } from './types';

/**
 * Pure state machine of the bank-of-changes runner: every user message, button
 * and server outcome is an action, and `reduceBankSession` turns it into the
 * next session (step, transcript, file list). Nothing here touches React, the
 * network or Word, so the whole conversation is unit-testable.
 *
 * The conversation itself is kept on the server (bankConversationSync.ts says
 * how); this is the pane's live view of it. A run (the look, the build, an
 * answer) goes on in the server: the pane asks for it, follows how far it got,
 * and when it is over reads the conversation again — the run's outcome is the
 * server's to write, so it is the same whether the pane stayed open or not.
 */

/** `accept` of the composer's paperclip for this workflow: Word files only,
 * including legacy `.doc` (the backend converts it). */
export const WORD_ATTACHMENT_ACCEPT =
  '.docx,.doc,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword';

/** Newly picked files travel as base64 inside ONE JSON request (the draft).
 * Base64 adds a third, and Cloud Run rejects a request over 32 MiB, so raw
 * files are capped well under that — an oversized upload gets a clear reply
 * here instead of an opaque gateway error after the lawyer has attached everything. */
export const MAX_TOTAL_UPLOAD_BYTES = 20 * 1024 * 1024;

export type BankAction =
  | { type: 'submit'; text: string; files: File[] }
  | { type: 'remove_appendix'; index: number }
  | { type: 'replace_bank' }
  | { type: 'finish_upload' }
  /** The server keeps the conversation as it was sent: the files that were sent are the server's from now on. */
  | {
      type: 'draft_saved';
      conversation: BankConversationInfo;
      sent: { bank: BankUpload | null; appendices: BankUpload[] };
    }
  | { type: 'analyze_failed'; message: string }
  | { type: 'request_more_appendices' }
  | { type: 'start_build' }
  /** The server took the run: it goes on there from now on, whatever becomes of the pane. */
  | { type: 'run_started'; conversation: BankConversationInfo }
  /** How far the run got, as the server says when asked. */
  | { type: 'run_progress'; run: BankServerRun }
  /** The run is over (or the conversation was read again for another reason): this is how it stands on the server. */
  | { type: 'conversation_loaded'; session: BankSessionState }
  | { type: 'build_failed'; message: string }
  | { type: 'revise_failed'; message: string }
  /** The lawyer pressed stop: the server is asked to stop the run, and says when it did. */
  | { type: 'stop' }
  /** The request to stop did not reach the server: the run goes on, and stop can be pressed again. */
  | { type: 'stop_failed' }
  | { type: 'notice'; content: string }
  | { type: 'restart' };

export function makeInitialBankSession(copy: BankOfChangesCopy): BankSessionState {
  return {
    step: 'awaiting_bank',
    turns: [{ id: 'bank-turn-0', role: 'assistant', content: copy.welcomeAskBank }],
    conversationId: null,
    bank: null,
    appendices: [],
    analysis: null,
    activity: null,
    version: 0,
    answerable: false,
    pendingRequest: null,
    following: false,
    stopping: false,
  };
}

/** Word's own lock files (`~$name.docx`) sit next to an open document and are
 * easy to pick up when selecting a whole folder — they are not documents. */
export function isWordFileName(name: string): boolean {
  const trimmed = name.trim();
  if (trimmed.startsWith('~$')) return false;
  return /\.(docx|doc)$/i.test(trimmed);
}

function fileNameKey(name: string): string {
  return name.normalize('NFC').trim().toLowerCase();
}

/** Two uploads are the same file when both name and size match. */
function fileIdentity(file: BankUpload): string {
  return `${fileNameKey(file.name)}\u0000${file.size}`;
}

function totalBytes(files: Array<BankUpload | null>): number {
  return files.reduce((sum, f) => sum + (f ? f.size : 0), 0);
}

export type AppendixAdditionOutcome = {
  /** The full list after the addition (existing order kept, new files last). */
  appendices: BankUpload[];
  added: File[];
  duplicates: File[];
  notWord: File[];
  sameAsBank: File[];
  tooLarge: File[];
};

/** Sorts one upload into what joins the appendix list and what is refused, and why. */
export function addAppendices(
  existing: BankUpload[],
  bank: BankUpload | null,
  incoming: File[],
  maxTotalBytes: number = MAX_TOTAL_UPLOAD_BYTES,
): AppendixAdditionOutcome {
  const outcome: AppendixAdditionOutcome = {
    appendices: [...existing],
    added: [],
    duplicates: [],
    notWord: [],
    sameAsBank: [],
    tooLarge: [],
  };
  const seen = new Set(existing.map(fileIdentity));
  const bankName = bank ? fileNameKey(bank.name) : null;
  let bytes = totalBytes([bank, ...existing]);

  for (const file of incoming) {
    if (!isWordFileName(file.name)) {
      outcome.notWord.push(file);
    } else if (bankName !== null && fileNameKey(file.name) === bankName) {
      outcome.sameAsBank.push(file);
    } else if (seen.has(fileIdentity(file))) {
      outcome.duplicates.push(file);
    } else if (bytes + file.size > maxTotalBytes) {
      outcome.tooLarge.push(file);
    } else {
      seen.add(fileIdentity(file));
      bytes += file.size;
      outcome.added.push(file);
      outcome.appendices.push(file);
    }
  }
  return outcome;
}

function names(files: File[]): string[] {
  return files.map((f) => f.name);
}

/** The agent's reply to an appendix upload: what was refused (one line per
 * reason), how many appendices it holds now, and what the lawyer can do next. */
export function describeAppendixAddition(
  outcome: AppendixAdditionOutcome,
  copy: BankOfChangesCopy,
  nextHint: string,
): string {
  const lines: string[] = [];
  if (outcome.notWord.length) lines.push(copy.skippedNotWord(names(outcome.notWord)));
  if (outcome.sameAsBank.length) lines.push(copy.skippedSameAsBank(names(outcome.sameAsBank)));
  if (outcome.duplicates.length) lines.push(copy.skippedDuplicates(names(outcome.duplicates)));
  if (outcome.tooLarge.length) lines.push(copy.skippedTooLarge(names(outcome.tooLarge)));
  const total = outcome.appendices.length;
  lines.push(
    outcome.added.length > 0 ? copy.appendicesAdded(outcome.added.length, total) : copy.nothingAdded(total),
  );
  lines.push(total > 0 ? nextHint : copy.needAtLeastOneAppendix);
  return lines.join('\n');
}

/** What the lawyer's own turn shows: their text, then one line per uploaded file. */
export function formatUserTurn(text: string, files: File[]): string {
  const lines = files.map((f) => `📎 ${f.name}`);
  const trimmed = text.trim();
  return (trimmed ? [trimmed, ...lines] : lines).join('\n');
}

/** A server message followed by its warnings under their own heading, one per line. */
export function withWarnings(message: string, warnings: string[] | undefined, heading: string): string {
  const lines = (warnings ?? []).map((w) => w.trim()).filter(Boolean);
  if (lines.length === 0) return message;
  return `${message}\n\n${heading}\n${lines.map((w) => `• ${w}`).join('\n')}`;
}

/** The stage the server names when a run is over; it closes the run and is no cluster of its own. */
const FINAL_STAGE = 'done';
/** A message that names no stage joins the cluster before it, or opens this one. */
const UNNAMED_STAGE = 'working';

/** Adds a server progress message to a run: to the last cluster when it is of
 * the same stage, otherwise as a new cluster. A message the server repeats
 * back-to-back is kept once. */
export function appendActivity(activity: BankActivity, stage: string | undefined, message: string): BankActivity {
  const line = message.trim();
  if (!line || stage === FINAL_STAGE) return activity;
  const last = activity.clusters[activity.clusters.length - 1];
  const name = (stage ?? '').trim() || last?.stage || UNNAMED_STAGE;
  if (last && last.stage === name) {
    if (last.lines[last.lines.length - 1] === line) return activity;
    return { ...activity, clusters: [...activity.clusters.slice(0, -1), { ...last, lines: [...last.lines, line] }] };
  }
  return { ...activity, clusters: [...activity.clusters, { stage: name, lines: [line] }] };
}

/** A line that says something is under way ("קורא את הנספח…"), as opposed to one that reports a result. */
function isUnderWay(line: string): boolean {
  return /(…|\.\.\.)$/.test(line.trim());
}

/** The lines of a cluster that report results, when it has any. */
function resultLines(cluster: BankActivityCluster): string[] {
  return cluster.lines.filter((line) => !isUnderWay(line));
}

/** "(3/12) …" at the head of the latest result of a cluster: how far a stage that counts its work has come. */
export function clusterProgress(cluster: BankActivityCluster): { done: number; total: number } | null {
  const results = resultLines(cluster);
  for (let i = results.length - 1; i >= 0; i -= 1) {
    const found = /^\((\d+)\s*\/\s*(\d+)\)/.exec(results[i].trim());
    if (found) return { done: Number(found[1]), total: Number(found[2]) };
  }
  return null;
}

/** What a cluster shows when it is opened. A stage that reported results shows
 * them and, while it is still running, what it is doing right now; "starting
 * X…" lines of work that has since reported are left out. */
export function clusterDetailLines(cluster: BankActivityCluster, running: boolean): string[] {
  const results = resultLines(cluster);
  if (results.length === 0) return cluster.lines;
  const last = cluster.lines[cluster.lines.length - 1];
  return running && isUnderWay(last) ? [...results, last] : results;
}

/** The title of a cluster, with its count when the stage counts its work. */
export function clusterTitle(cluster: BankActivityCluster, copy: BankOfChangesCopy): string {
  const progress = clusterProgress(cluster);
  const title = copy.stageTitle(cluster.stage);
  return progress ? `${title} · ${copy.stageProgress(progress.done, progress.total)}` : title;
}

/** One line for a whole run. While it runs: what it is doing now. When it is
 * over: what was done, the way a finished tool run is summed up. */
export function activitySummary(activity: BankActivity, copy: BankOfChangesCopy): string {
  const current = activity.clusters[activity.clusters.length - 1];
  if (activity.state === 'running') {
    if (!current) return activity.kind === 'build' ? copy.buildStarting : copy.reviseStarting;
    return clusterProgress(current) ? `${clusterTitle(current, copy)}…` : current.lines[current.lines.length - 1];
  }
  if (activity.state === 'failed') return copy.activityFailed;
  if (activity.state === 'stopped') return copy.activityStopped;
  const reading = activity.clusters.find((cluster) => cluster.stage === 'reading');
  const appendices = reading ? (clusterProgress(reading)?.done ?? resultLines(reading).length) : 0;
  return copy.activityDone(activity.kind, appendices, activity.clusters.length);
}

function normalizeShortMessage(text: string): string {
  return text
    .trim()
    .replace(/[.!?,;:"'״׳]+/g, '')
    .replace(/\s+/g, ' ')
    .toLowerCase();
}

const DONE_PREFIXES = ['סיימתי', 'זהו', 'זה הכל', 'זה הכול', 'אין עוד', 'אלה כל', 'אלו כל'];
const START_MESSAGES = new Set([
  'התחל',
  'התחילו',
  'להתחיל',
  'אפשר להתחיל',
  'כן',
  'כן התחל',
  'אישור',
  'מאשר',
  'מאשרת',
  'קדימה',
]);

/** A short typed "I'm done uploading" — the same as pressing the button. Kept
 * to short messages so a longer sentence that merely opens with such a word
 * does not start the analysis. */
export function isDoneMessage(text: string): boolean {
  const normalized = normalizeShortMessage(text);
  if (!normalized || normalized.split(' ').length > 4) return false;
  return DONE_PREFIXES.some((p) => normalized === p || normalized.startsWith(`${p} `));
}

/** A typed confirmation to start building — the whole message must be one,
 * because a build runs for minutes. */
export function isStartMessage(text: string): boolean {
  return START_MESSAGES.has(normalizeShortMessage(text));
}

function appendTurns(
  state: BankSessionState,
  ...turns: Array<{ role: BankChatTurn['role']; content: string; files?: BankOutputFile[]; activity?: BankActivity } | null>
): BankChatTurn[] {
  const next = [...state.turns];
  for (const turn of turns) {
    if (!turn || !turn.content) continue;
    const added: BankChatTurn = { id: `bank-turn-${next.length}`, role: turn.role, content: turn.content };
    if (turn.files && turn.files.length > 0) added.files = turn.files;
    if (turn.activity) added.activity = turn.activity;
    next.push(added);
  }
  return next;
}

const user = (content: string) => ({ role: 'user' as const, content });
const assistant = (content: string, files?: BankOutputFile[]) => ({ role: 'assistant' as const, content, files });

function submitBank(state: BankSessionState, text: string, files: File[], copy: BankOfChangesCopy): BankSessionState {
  const userTurn = user(formatUserTurn(text, files));
  const refuse = (reply: string): BankSessionState => ({
    ...state,
    turns: appendTurns(state, userTurn, assistant(reply)),
  });

  if (files.length === 0) return refuse(copy.bankMissingFile);
  if (files.length > 1) return refuse(copy.bankOnlyOneFile);
  const file = files[0];
  if (!isWordFileName(file.name)) return refuse(copy.onlyWordFiles);
  if (state.appendices.some((a) => fileNameKey(a.name) === fileNameKey(file.name))) {
    return refuse(copy.bankSameAsAppendix(file.name));
  }
  if (file.size + totalBytes(state.appendices) > MAX_TOTAL_UPLOAD_BYTES) return refuse(copy.filesTooLarge);

  const reply =
    state.appendices.length === 0
      ? copy.bankReceivedAskAppendices(file.name)
      : copy.bankReplaced(file.name, state.appendices.length);
  return {
    ...state,
    step: 'awaiting_appendices',
    bank: file,
    analysis: null,
    turns: appendTurns(state, userTurn, assistant(reply)),
  };
}

function beginAnalyze(state: BankSessionState, userContent: string, copy: BankOfChangesCopy): BankSessionState {
  if (state.appendices.length === 0) {
    return { ...state, turns: appendTurns(state, user(userContent), assistant(copy.needAtLeastOneAppendix)) };
  }
  return {
    ...state,
    step: 'analyzing',
    turns: appendTurns(state, user(userContent), assistant(copy.analyzing)),
  };
}

function beginBuild(state: BankSessionState, userContent: string, copy: BankOfChangesCopy): BankSessionState {
  if (state.appendices.length === 0) {
    return { ...state, turns: appendTurns(state, user(userContent), assistant(copy.needAtLeastOneAppendix)) };
  }
  return {
    ...state,
    step: 'building',
    activity: { kind: 'build', state: 'running', clusters: [] },
    turns: appendTurns(state, user(userContent)),
  };
}

function submitAppendices(
  state: BankSessionState,
  text: string,
  files: File[],
  copy: BankOfChangesCopy,
): BankSessionState {
  const confirming = state.step === 'awaiting_confirmation';

  if (files.length === 0) {
    if (!confirming && isDoneMessage(text)) return beginAnalyze(state, text.trim(), copy);
    if (confirming && isStartMessage(text)) return beginBuild(state, text.trim(), copy);
    const hint = confirming ? copy.confirmationTextHint : copy.appendicesTextHint;
    return { ...state, turns: appendTurns(state, user(text.trim()), assistant(hint)) };
  }

  // Files added after the analysis do not re-run it: the layout describes the
  // bank only, and `build` always receives the full current list.
  const outcome = addAppendices(state.appendices, state.bank, files);
  const reply = describeAppendixAddition(
    outcome,
    copy,
    confirming ? copy.confirmNextHint : copy.appendicesNextHint,
  );
  return {
    ...state,
    appendices: outcome.appendices,
    turns: appendTurns(state, user(formatUserTurn(text, files)), assistant(reply)),
  };
}

/** After the files are ready the lawyer answers the agent's follow-up, or asks
 * for any correction, in their own words. Every message goes to the server. */
function submitFollowup(state: BankSessionState, text: string, files: File[], copy: BankOfChangesCopy): BankSessionState {
  if (!state.answerable || !state.conversationId) return state;
  if (files.length > 0) {
    return { ...state, turns: appendTurns(state, user(formatUserTurn(text, files)), assistant(copy.followupNoFiles)) };
  }
  const request = text.trim();
  if (!request) return state;
  return {
    ...state,
    step: 'revising',
    pendingRequest: request,
    activity: { kind: 'revise', state: 'running', clusters: [] },
    turns: appendTurns(state, user(request)),
  };
}

/** The turn that hands a version over: what the files are, with their buttons under it. */
export function versionTurn(files: BankVersionFiles, copy: BankOfChangesCopy) {
  const { version } = files;
  const hint =
    version <= 1
      ? files.refined
        ? copy.outputHintTwoFiles
        : copy.outputHint
      : copy.outputHintVersion(version, files.refined !== null);
  return assistant(hint, outputFiles(files, copy));
}

/** After a draft was saved: every file that was sent is the server's now, so it is not sent again.
 * A file the lawyer added while the draft was on its way stays as it is, for the next draft. */
function adoptStoredFiles(
  state: BankSessionState,
  conversation: BankConversationInfo,
  sent: { bank: BankUpload | null; appendices: BankUpload[] },
): BankSessionState {
  // The server may keep a file under another name and size (a legacy .doc is kept as .docx). The pane
  // goes on knowing it as the lawyer picked it, so that the same file picked again is still a duplicate.
  const stored = new Map<BankUpload, BankStoredFile>();
  const keep = (file: BankUpload, kept: BankStoredFile | null | undefined) => {
    if (kept) stored.set(file, { id: kept.id, name: file.name, size: file.size });
  };
  if (sent.bank) keep(sent.bank, conversation.bank);
  sent.appendices.forEach((file, index) => keep(file, conversation.appendices[index]));
  return {
    ...state,
    conversationId: conversation.id,
    bank: state.bank ? (stored.get(state.bank) ?? state.bank) : null,
    appendices: state.appendices.map((file) => stored.get(file) ?? file),
  };
}

export function reduceBankSession(
  state: BankSessionState,
  action: BankAction,
  copy: BankOfChangesCopy,
): BankSessionState {
  switch (action.type) {
    case 'submit':
      if (state.step === 'awaiting_bank') return submitBank(state, action.text, action.files, copy);
      if (state.step === 'awaiting_appendices' || state.step === 'awaiting_confirmation') {
        return submitAppendices(state, action.text, action.files, copy);
      }
      if (state.step === 'done') return submitFollowup(state, action.text, action.files, copy);
      return state;

    case 'remove_appendix': {
      if (state.step !== 'awaiting_appendices' && state.step !== 'awaiting_confirmation') return state;
      const removed = state.appendices[action.index];
      if (!removed) return state;
      const appendices = state.appendices.filter((_, i) => i !== action.index);
      return {
        ...state,
        appendices,
        turns: appendTurns(state, assistant(copy.appendixRemoved(removed.name, appendices.length))),
      };
    }

    case 'replace_bank':
      // A different bank needs a fresh analysis; the appendices stay.
      if (state.step !== 'awaiting_appendices' && state.step !== 'awaiting_confirmation') return state;
      return {
        ...state,
        step: 'awaiting_bank',
        bank: null,
        analysis: null,
        turns: appendTurns(state, assistant(copy.askBankReplacement)),
      };

    case 'finish_upload':
      if (state.step !== 'awaiting_appendices') return state;
      return beginAnalyze(state, copy.doneUploading, copy);

    case 'draft_saved':
      return adoptStoredFiles(state, action.conversation, action.sent);

    case 'analyze_failed':
      if (state.step !== 'analyzing') return state;
      return {
        ...state,
        step: 'awaiting_appendices',
        following: false,
        stopping: false,
        turns: appendTurns(state, assistant(`${action.message}\n\n${copy.afterAnalyzeError}`)),
      };

    case 'request_more_appendices':
      if (state.step !== 'awaiting_confirmation') return state;
      return {
        ...state,
        turns: appendTurns(state, user(copy.addMoreAppendices), assistant(copy.addMorePrompt)),
      };

    case 'start_build':
      if (state.step !== 'awaiting_confirmation' || !state.analysis) return state;
      return beginBuild(state, copy.start, copy);

    case 'run_started':
      if (!isRunning(state)) return state;
      return {
        ...state,
        conversationId: action.conversation.id,
        following: true,
        stopping: Boolean(action.conversation.run?.stop_requested),
      };

    case 'run_progress':
      if (!isRunning(state) || !state.following) return state;
      return {
        ...state,
        // The look reports no stages; a build or a revision is shown as far as the server says it got.
        activity: action.run.activity ? { ...action.run.activity, state: 'running' } : state.activity,
        stopping: state.stopping || action.run.stop_requested,
      };

    case 'conversation_loaded':
      return action.session;

    case 'revise_failed':
      if (state.step !== 'revising') return state;
      return {
        ...state,
        step: 'done',
        pendingRequest: null,
        activity: null,
        following: false,
        stopping: false,
        turns: appendTurns(state, assistant(`${action.message}\n\n${copy.afterReviseError}`)),
      };

    case 'build_failed':
      if (state.step !== 'building') return state;
      return {
        ...state,
        step: 'awaiting_confirmation',
        activity: null,
        following: false,
        stopping: false,
        turns: appendTurns(state, assistant(`${action.message}\n\n${copy.afterBuildError}`)),
      };

    case 'stop':
      // Only a run the server has can be stopped; it stops there, and the conversation is read again when it did.
      if (!isRunning(state) || !state.following) return state;
      return { ...state, stopping: true };

    case 'stop_failed':
      return state.stopping ? { ...state, stopping: false } : state;

    case 'notice':
      return { ...state, turns: appendTurns(state, assistant(action.content)) };

    case 'restart':
      return makeInitialBankSession(copy);

    default:
      return state;
  }
}

/** The name a generated file is saved under — the server's, or a default. */
export function outputFilename(file: Pick<BankGeneratedFile, 'filename'>, fallback: string): string {
  const name = (file.filename || '').trim() || fallback;
  return /\.docx$/i.test(name) ? name : `${name}.docx`;
}

/** The files of one version as a turn offers them, one card each: one file, or
 * two when the refinement was completed. From the second version on, each card
 * names its version, so the files of two versions in the chat are told apart. */
export function outputFiles(files: BankVersionFiles, copy: BankOfChangesCopy): BankOutputFile[] {
  const { version } = files;
  const card = (kind: BankOutputFile['kind'], label: string, file: BankGeneratedFile, fallback: string): BankOutputFile => ({
    key: `v${version}-${kind}`,
    kind,
    version,
    label,
    detail: copy.fileDetail(version),
    downloadLabel: copy.downloadAria(label, version),
    filename: outputFilename(file, fallback),
    id: file.id,
  });
  if (!files.refined) return [card('built', copy.singleFileLabel, files.built, copy.defaultOutputFilename)];
  return [
    card('built', copy.builtFileLabel, files.built, copy.defaultOutputFilename),
    card('refined', copy.refinedFileLabel, files.refined, copy.defaultRefinedFilename),
  ];
}

/** A notice about one of the output files, naming it: with two files in the chat, "the download" alone says too little. */
export function fileNotice(file: BankOutputFile, text: string): string {
  return `"${file.filename}" — ${text}`;
}

/** Whether a server call is running in this state. */
export function isRunning(state: BankSessionState): boolean {
  return state.step === 'analyzing' || state.step === 'building' || state.step === 'revising';
}

/** The turns a session would add right now, appended to it. For rebuilding a conversation from the server's record. */
export function withTurns(
  state: BankSessionState,
  ...turns: Array<{ role: BankChatTurn['role']; content: string; files?: BankOutputFile[]; activity?: BankActivity } | null>
): BankSessionState {
  return { ...state, turns: appendTurns(state, ...turns) };
}
